import { useCallback, useMemo, useRef, useState } from 'react';

import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core';
import { unwrapResult } from '@reduxjs/toolkit';
import { getIn, useFormikContext } from 'formik';

import { useAppDispatch } from 'store';
import {
  FormFieldHeaderTemplate,
  FormFieldTemplate,
  MessageHeaderFormatsTemplate,
  uploadTemplateDocument,
  uploadTemplateImage,
  uploadTemplateVideo,
} from 'store/reducers/templates';
import {
  FILE_SIZE_ERROR_MESSAGES,
  MAX_FILE_SIZES,
  MessageVariables,
} from 'utils/validators';

interface FormFieldsTemplateProps {
  formFields: FormFieldTemplate | null;
}

interface FileUploadButtonProps {
  label: string;
  accept: string;
  onFileSelect: (file: File | null) => void;
  fileName?: string;
  error?: boolean;
  uploading?: boolean;
  uploadErrorMessage?: string;
}

const UPLOAD_CONFIG = {
  [MessageHeaderFormatsTemplate.IMAGEM]: {
    title: 'Imagem',
    label: 'Selecionar imagem',
    accept: 'image/*',
  },
  [MessageHeaderFormatsTemplate.VIDEO]: {
    title: 'Vídeo',
    label: 'Selecionar vídeo',
    accept: 'video/*',
  },
  [MessageHeaderFormatsTemplate.DOCUMENTO]: {
    title: 'Documento',
    label: 'Selecionar documento',
    accept: '.pdf,.doc,.docx',
  },
} as const;

const useStyles = makeStyles(theme => ({
  typeLabel: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minWidth: 100,
    height: 36,
    backgroundColor: theme.palette.action.disabledBackground,
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadButton: {
    minWidth: 0,
    height: 36,
    backgroundColor: 'transparent',
    border: (props: { hasError?: boolean; uploading?: boolean }) =>
      `1.5px dashed ${
        props.hasError
          ? theme.palette.error.main
          : alpha(theme.palette.text.secondary, 0.4)
      }`,
    borderRadius: '8px',
    cursor: (props: { hasError?: boolean; uploading?: boolean }) =>
      props.uploading ? 'not-allowed' : 'pointer',
    textAlign: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    overflow: 'hidden',
    transition: theme.transitions.create(['background-color', 'border-color']),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      borderColor: (props: { hasError?: boolean; uploading?: boolean }) =>
        props.hasError ? theme.palette.error.main : theme.palette.primary.main,
    },
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileName: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  variableFieldError: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 500,
  },
}));

function TypeLabel({ children }: { children: React.ReactNode }) {
  const classes = useStyles({});
  return <Box className={classes.typeLabel}>{children}</Box>;
}

function FileUploadButton({
  label,
  accept,
  onFileSelect,
  fileName,
  error,
  uploading,
  uploadErrorMessage,
}: FileUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasError = error || Boolean(uploadErrorMessage);
  const classes = useStyles({ hasError, uploading });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileSelect(event.target.files?.[0] ?? null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <Box width="100%" minWidth={0} display="flex" flexDirection="column">
      <Box component="label" className={classes.uploadButton}>
        {uploading ? (
          <Box display="flex" flexDirection="row" alignItems="center">
            <CircularProgress size={18} style={{ marginRight: 8 }} />
            <Typography variant="body2">Enviando...</Typography>
          </Box>
        ) : (
          <Typography variant="body2" className={classes.fileName}>
            {fileName || label}
          </Typography>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={handleChange}
          disabled={uploading}
        />
      </Box>

      {uploadErrorMessage ? (
        <Typography
          variant="caption"
          color="error"
          className={classes.variableFieldError}
        >
          {uploadErrorMessage}
        </Typography>
      ) : (
        error && (
          <Typography
            variant="caption"
            color="error"
            className={classes.variableFieldError}
          >
            Este campo é obrigatório
          </Typography>
        )
      )}
    </Box>
  );
}

function VariableField({
  variableName,
  name,
}: {
  variableName: string;
  name: string;
}) {
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext<{ messageVariables: MessageVariables }>();
  const classes = useStyles({});

  const fieldValue = getIn(values, name);
  const fieldError = getIn(errors, name);
  const isTouched = getIn(touched, name);

  return (
    <Box display="flex" flexDirection="row" width="100%" minWidth={0} mb={1}>
      <TypeLabel>{`{{${variableName}}}`}</TypeLabel>

      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        ml={1}
        style={{ minWidth: 0 }}
      >
        <TextField
          name={name}
          variant="outlined"
          placeholder="Digite"
          fullWidth
          value={fieldValue || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(isTouched && fieldError)}
        />
        {isTouched && fieldError && (
          <Typography
            variant="caption"
            color="error"
            className={classes.variableFieldError}
          >
            {fieldError}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function HeaderField({
  header,
  headerError,
  uploading,
  uploadErrorMessage,
  onFileSelect,
}: {
  header: FormFieldHeaderTemplate;
  headerError: boolean;
  uploading: boolean;
  uploadErrorMessage?: string;
  onFileSelect: (file: File | null) => void;
}) {
  const { values, setFieldValue } = useFormikContext<{
    messageVariables: MessageVariables;
  }>();
  const uploadConfig = header.type
    ? UPLOAD_CONFIG[header.type as keyof typeof UPLOAD_CONFIG]
    : null;

  if (uploadConfig) {
    const file = values.messageVariables?.headerFile;

    return (
      <Box display="flex" flexDirection="row" width="100%" minWidth={0}>
        <TypeLabel>{uploadConfig.title}</TypeLabel>

        <Box flex={1} ml={1} style={{ minWidth: 0 }}>
          <FileUploadButton
            {...uploadConfig}
            fileName={file?.name}
            error={headerError}
            uploading={uploading}
            uploadErrorMessage={uploadErrorMessage}
            onFileSelect={newFile => {
              setFieldValue('messageVariables.headerFile', newFile);
              if (newFile) {
                onFileSelect(newFile);
              }
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <>
      {header.variables?.map((variable, index) => (
        <VariableField
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          variableName={variable.name || `Variável ${index + 1}`}
          name={`messageVariables.headerVariables.${index}.value`}
        />
      ))}
    </>
  );
}

function FormFieldsTemplate({ formFields }: FormFieldsTemplateProps) {
  const { setFieldValue, errors, touched } = useFormikContext<{
    messageVariables: MessageVariables;
  }>();
  const classes = useStyles({});

  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<
    string | undefined
  >();

  const uploadMap = useMemo(
    () => ({
      [MessageHeaderFormatsTemplate.IMAGEM]: (file: File) =>
        dispatch(uploadTemplateImage(file)),
      [MessageHeaderFormatsTemplate.VIDEO]: (file: File) =>
        dispatch(uploadTemplateVideo(file)),
      [MessageHeaderFormatsTemplate.DOCUMENTO]: (file: File) =>
        dispatch(uploadTemplateDocument(file)),
    }),
    [dispatch],
  );

  const handleUpload = useCallback(
    async (file: File, type: MessageHeaderFormatsTemplate) => {
      try {
        setUploadErrorMessage(undefined);

        const maxSize = MAX_FILE_SIZES[type];
        if (maxSize && file.size > maxSize) {
          setUploadErrorMessage(
            FILE_SIZE_ERROR_MESSAGES[type] || 'O arquivo é muito grande.',
          );
          setFieldValue('messageVariables.header.url', '');
          setFieldValue('messageVariables.header.storage_id', '');
          return;
        }

        setFieldValue('messageVariables.header.url', '');
        setFieldValue('messageVariables.header.storage_id', '');

        setUploading(true);
        const resultAction = await uploadMap[type](file);
        const response = unwrapResult(resultAction);
        setFieldValue('messageVariables.header.url', response.url);
        setFieldValue('messageVariables.header.storage_id', response.storageId);
      } catch (error) {
        const apiMessage = (error as { message?: string })?.message;
        setUploadErrorMessage(
          apiMessage ?? 'Erro ao enviar arquivo. Tente novamente.',
        );
        setFieldValue('messageVariables.header.url', '');
        setFieldValue('messageVariables.header.storage_id', '');
      } finally {
        setUploading(false);
      }
    },
    [uploadMap, setFieldValue],
  );

  const isEmpty = !formFields || Object.keys(formFields).length === 0;
  if (isEmpty) {
    return null;
  }

  const headerError = Boolean(
    getIn(touched, 'messageVariables.header') &&
      getIn(errors, 'messageVariables.header'),
  );
  const hasBodyVariables = (formFields.body?.variables?.length ?? 0) > 0;

  return (
    <Box display="flex" mt={2} flexDirection="column" width="100%" minWidth={0}>
      {formFields.header && (
        <Box display="flex" flexDirection="column" minWidth={0} mb={2}>
          <Typography
            variant="body2"
            className={classes.sectionTitle}
            style={{ marginBottom: 8 }}
            color="textSecondary"
          >
            Preencha as variáveis do cabeçalho da mensagem.
          </Typography>

          <HeaderField
            header={formFields.header}
            headerError={headerError}
            uploading={uploading}
            uploadErrorMessage={uploadErrorMessage}
            onFileSelect={file => {
              setUploadErrorMessage(undefined);
              if (file) {
                const type = formFields?.header?.type;
                if (type) handleUpload(file, type);
              } else {
                setFieldValue('messageVariables.header.url', '');
                setFieldValue('messageVariables.header.storage_id', '');
              }
            }}
          />
        </Box>
      )}

      {hasBodyVariables && (
        <Box display="flex" flexDirection="column" minWidth={0}>
          <Typography
            variant="body2"
            className={classes.sectionTitle}
            style={{ marginBottom: 8 }}
            color="textSecondary"
          >
            Preencha as variáveis do corpo da mensagem.
          </Typography>

          {formFields.body?.variables?.map((variable, index) => (
            <VariableField
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              variableName={variable.name || `Variável ${index + 1}`}
              name={`messageVariables.body.variables.${index}.value`}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default FormFieldsTemplate;
